/**
 * Notification & Invitation Service
 * Handles API communication for /v1/notifications and /v1/invitations endpoints
 */

import { tokenManager } from '../utils/tokenManager'

// ─── Types ────────────────────────────────────────────────────────────────────

// Known notification types. Kept as a widened literal union (via `string & {}`)
// rather than a strict enum so an unrecognized type from the backend still
// type-checks — every type already renders generically via title/body.
export type NotificationType = 'group_invitation' | 'meal_plan_reused' | (string & {})

// Payload shape for `type: 'meal_plan_reused'`, sent to every other group
// member when someone reuses a meal plan into that group.
export interface MealPlanReusedPayload {
  group_id: number
  group_name: string
  creator_name: string
  meal_count: number
}

export interface Notification {
  id: number
  type: NotificationType
  title: string
  body: string
  payload: Record<string, unknown>
  is_read: boolean
  created_at: string
}

export interface NotificationListResponse {
  notifications: Notification[]
  total: number
  unread_count: number
  page: number
  page_size: number
  total_pages: number
}

export interface Invitation {
  id: number
  type: string
  inviter_user_id: number
  invitee_user_id: number
  resource_id: number
  resource_type: string
  status: 'pending' | 'accepted' | 'declined'
  payload: {
    group_name?: string
    inviter_name?: string
    [key: string]: unknown
  }
  created_at: string
  updated_at: string
}

export interface InvitationListResponse {
  invitations: Invitation[]
  total: number
}

export interface NotificationGetParams {
  is_read?: boolean
  limit?: number
  offset?: number
  page?: number
}

// ─── Error class ──────────────────────────────────────────────────────────────

export class NotificationError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string
  ) {
    super(message)
    this.name = 'NotificationError'
  }
}

// ─── Service ──────────────────────────────────────────────────────────────────

class NotificationService {
  private readonly notificationsUrl = '/v1/notifications'
  private readonly invitationsUrl = '/v1/invitations'

  private async getAuthHeaders(): Promise<HeadersInit> {
    const token = await tokenManager.getAccessToken()
    if (!token) {
      throw new NotificationError('No access token available', 401)
    }
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  }

  private async makeRequest<T>(url: string, options: RequestInit = {}): Promise<T> {
    const authHeaders = await this.getAuthHeaders()
    const response = await fetch(url, {
      ...options,
      headers: { ...authHeaders, ...options.headers }
    })

    if (!response.ok) {
      const body = await response.json().catch(() => ({})) as { error?: string; code?: string }
      throw new NotificationError(
        body.error ?? `HTTP ${response.status}: ${response.statusText}`,
        response.status,
        body.code
      )
    }

    // Some endpoints return 200 with a JSON body, others (PATCH) return simple messages
    return response.json() as Promise<T>
  }

  // ── Notifications ──────────────────────────────────────────────────────────

  getNotifications(params: NotificationGetParams = {}): Promise<NotificationListResponse> {
    const query = new URLSearchParams()
    if (params.is_read !== undefined) query.set('is_read', String(params.is_read))
    if (params.limit !== undefined) query.set('limit', String(params.limit))
    if (params.offset !== undefined) query.set('offset', String(params.offset))
    if (params.page !== undefined) query.set('page', String(params.page))
    const qs = query.toString()
    return this.makeRequest<NotificationListResponse>(
      `${this.notificationsUrl}${qs ? `?${qs}` : ''}`
    )
  }

  markAsRead(id: number): Promise<{ message: string }> {
    return this.makeRequest<{ message: string }>(
      `${this.notificationsUrl}/${id}/read`,
      { method: 'PATCH' }
    )
  }

  markAllAsRead(): Promise<{ message: string }> {
    return this.makeRequest<{ message: string }>(
      `${this.notificationsUrl}/read-all`,
      { method: 'POST' }
    )
  }

  deleteAllNotifications(): Promise<{ deleted: number }> {
    return this.makeRequest<{ deleted: number }>(
      this.notificationsUrl,
      { method: 'DELETE' }
    )
  }

  // ── Invitations ────────────────────────────────────────────────────────────

  getInvitations(status?: 'pending' | 'accepted' | 'declined'): Promise<InvitationListResponse> {
    const qs = status ? `?status=${status}` : ''
    return this.makeRequest<InvitationListResponse>(`${this.invitationsUrl}${qs}`)
  }

  acceptInvitation(id: number): Promise<Invitation> {
    return this.makeRequest<Invitation>(
      `${this.invitationsUrl}/${id}/accept`,
      { method: 'POST' }
    )
  }

  declineInvitation(id: number): Promise<Invitation> {
    return this.makeRequest<Invitation>(
      `${this.invitationsUrl}/${id}/decline`,
      { method: 'POST' }
    )
  }
}

export const notificationService = new NotificationService()
